import { useState, useEffect, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { AppLayout } from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { 
  getApplication, 
  ApplicationRecord,
  getApplicationReport,
  ReportRecord,
  ReportDocumentRecord,
  ReportEquipmentRecord,
  REPORT_DOCUMENT_LABELS,
  ReportDocumentType,
  uploadReportFile,
} from "@/lib/api/applications";
import { directus } from "@/lib/directus";
import { createItem, updateItem, deleteItem } from "@directus/sdk";
import { cn } from "@/lib/utils";

function ArrowLeftIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
    </svg>
  );
}

function UploadIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
    </svg>
  );
}

function FileIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
    </svg>
  );
}

function CheckCircleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function TrashIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
    </svg>
  );
}

function PlusIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
    </svg>
  );
}

// Equipment item form interface
interface EquipmentItemForm {
  id?: number;
  tempId?: string; // For new items not yet saved
  name: string;
  serial_number: string;
  quantity: number;
  price_with_vat: number;
  photo_id: string | null;
  photo_file_name?: string; // For display purposes
}

// Note: "report_photos" removed - photos are now uploaded per equipment item
const DOCUMENT_TYPES: ReportDocumentType[] = [
  "report_approval_decision",
  "report_bank_statement", 
  "report_invoices",
];

const REPORT_DOCUMENT_DESCRIPTIONS: Record<ReportDocumentType, string> = {
  report_approval_decision: "Рішення зборів співвласників / органу управління про затвердження звіту",
  report_bank_statement: "Виписка з банківського рахунку, що підтверджує перерахування коштів продавцю",
  report_invoices: "Видаткові накладні або акти приймання-передавання обладнання, підписані сторонами",
  report_photos: "Фото-, відеофіксація придбаного та встановленого обладнання",
};

const DOCUMENT_FORMATS: Record<ReportDocumentType, string> = {
  report_approval_decision: "PDF, JPG, PNG до 10 МБ",
  report_bank_statement: "PDF до 10 МБ",
  report_invoices: "PDF, JPG, PNG до 10 МБ",
  report_photos: "JPG, PNG, MP4, MOV до 50 МБ",
};

const DOCUMENT_ACCEPT: Record<ReportDocumentType, string> = {
  report_approval_decision: ".pdf,.jpg,.jpeg,.png",
  report_bank_statement: ".pdf",
  report_invoices: ".pdf,.jpg,.jpeg,.png",
  report_photos: ".jpg,.jpeg,.png,.mp4,.mov",
};

interface DocumentUploadCardProps {
  docType: ReportDocumentType;
  document: ReportDocumentRecord | null;
  onUpload: (file: File) => void;
  onRemove: () => void;
  isUploading: boolean;
  canEdit: boolean;
}

function DocumentUploadCard({ docType, document, onUpload, onRemove, isUploading, canEdit }: DocumentUploadCardProps) {
  const [isDragOver, setIsDragOver] = useState(false);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      if (!canEdit) return;
      const file = e.dataTransfer.files[0];
      if (file) {
        onUpload(file);
      }
    },
    [onUpload, canEdit]
  );

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      onUpload(file);
    }
    e.target.value = "";
  };

  return (
    <div className="bg-white rounded-2xl border-[3px] border-border/50 overflow-hidden">
      <div className="p-4">
        <div className="flex items-start gap-3">
          <div
            className={cn(
              "w-10 h-10 rounded-xl flex items-center justify-center shrink-0",
              document ? "bg-green-100" : "bg-muted"
            )}
          >
            {document ? (
              <CheckCircleIcon className="w-5 h-5 text-green-600" />
            ) : (
              <FileIcon className="w-5 h-5 text-muted-foreground" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-medium">{REPORT_DOCUMENT_LABELS[docType]}</p>
            <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-2">
              {REPORT_DOCUMENT_DESCRIPTIONS[docType]}
            </p>
          </div>
        </div>
      </div>

      {document ? (
        <div className="px-4 pb-4">
          <div className="flex items-center gap-3 p-3 bg-green-50 rounded-xl">
            <FileIcon className="w-5 h-5 text-green-600 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-[12px] font-medium text-green-700 truncate">{document.file_name}</p>
              <p className="text-[10px] text-green-600">Завантажено</p>
            </div>
            {canEdit && (
              <button
                type="button"
                onClick={onRemove}
                className="p-2 rounded-lg hover:bg-green-100 transition-colors"
              >
                <TrashIcon className="w-4 h-4 text-green-600" />
              </button>
            )}
          </div>
        </div>
      ) : canEdit ? (
        <div className="px-4 pb-4">
          {isUploading ? (
            <div className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-primary/50 rounded-xl bg-primary/5">
              <Spinner size="md" className="mb-2" />
              <p className="text-[12px] font-medium text-primary">Завантаження...</p>
            </div>
          ) : (
            <label
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragOver(true);
              }}
              onDragLeave={() => setIsDragOver(false)}
              onDrop={handleDrop}
              className={cn(
                "flex flex-col items-center justify-center p-6 border-2 border-dashed rounded-xl cursor-pointer transition-all",
                isDragOver
                  ? "border-primary bg-primary/5"
                  : "border-border/50 hover:border-primary/50 hover:bg-muted/30"
              )}
            >
              <UploadIcon className={cn("w-8 h-8 mb-2", isDragOver ? "text-primary" : "text-muted-foreground")} />
              <p className="text-[12px] font-medium text-center">
                Перетягніть файл або <span className="text-primary">оберіть</span>
              </p>
              <p className="text-[10px] text-muted-foreground mt-1">{DOCUMENT_FORMATS[docType]}</p>
              <input
                type="file"
                accept={DOCUMENT_ACCEPT[docType]}
                onChange={handleFileChange}
                className="hidden"
              />
            </label>
          )}
        </div>
      ) : (
        <div className="px-4 pb-4">
          <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
            <FileIcon className="w-5 h-5 text-slate-400 shrink-0" />
            <p className="text-[12px] text-slate-500">Документ не завантажено</p>
          </div>
        </div>
      )}
    </div>
  );
}

export function ReportWizard() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [application, setApplication] = useState<ApplicationRecord | null>(null);
  const [report, setReport] = useState<ReportRecord | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState<ReportDocumentType | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Documents state (report_photos removed - photos are per equipment item now)
  const [documents, setDocuments] = useState<Partial<Record<ReportDocumentType, ReportDocumentRecord | null>>>({
    report_approval_decision: null,
    report_bank_statement: null,
    report_invoices: null,
  });

  // Equipment items state
  const [equipmentItems, setEquipmentItems] = useState<EquipmentItemForm[]>([]);
  const [isSavingEquipment, setIsSavingEquipment] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    amount_received: "",
    amount_used: "",
    own_funds_used: "",
    unused_funds: "",
    equipment_installed: false,
    equipment_tested: false,
    installation_date: "",
    notes: "",
  });

  useEffect(() => {
    async function loadData() {
      if (!id) return;
      setIsLoading(true);
      try {
        const appData = await getApplication(id);
        if (!appData) {
          setError("Заявку не знайдено");
          return;
        }
        setApplication(appData);

        // Load existing report
        const reportData = await getApplicationReport(appData.id);
        if (reportData) {
          setReport(reportData);
          setFormData({
            amount_received: reportData.amount_received?.toString() || "",
            amount_used: reportData.amount_used?.toString() || "",
            own_funds_used: reportData.own_funds_used?.toString() || "",
            unused_funds: reportData.unused_funds?.toString() || "",
            equipment_installed: reportData.equipment_installed || false,
            equipment_tested: reportData.equipment_tested || false,
            installation_date: reportData.installation_date || "",
            notes: reportData.notes || "",
          });
          
          // Load existing documents (report_photos handled per equipment item now)
          if (reportData.documents) {
            const docsMap: Partial<Record<ReportDocumentType, ReportDocumentRecord | null>> = {
              report_approval_decision: null,
              report_bank_statement: null,
              report_invoices: null,
            };
            reportData.documents.forEach(doc => {
              if (doc.document_type in docsMap) {
                docsMap[doc.document_type as ReportDocumentType] = doc;
              }
            });
            setDocuments(docsMap);
          }
          
          // Load existing equipment items
          if (reportData.equipment && reportData.equipment.length > 0) {
            setEquipmentItems(
              reportData.equipment.map(eq => ({
                id: eq.id,
                name: eq.name || "",
                serial_number: eq.serial_number || "",
                quantity: eq.quantity || 1,
                price_with_vat: eq.price_with_vat || 0,
                photo_id: eq.photo_id || null,
              }))
            );
          }
        } else {
          // Pre-fill with payment amount
          setFormData(prev => ({
            ...prev,
            amount_received: appData.payment_amount?.toString() || "",
          }));
        }
      } catch {
        setError("Помилка завантаження даних");
      } finally {
        setIsLoading(false);
      }
    }
    loadData();
  }, [id]);

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setSuccessMessage(null);
  };

  const ensureReportExists = async (): Promise<number | null> => {
    if (!application) return null;
    
    if (report) return report.id;

    // Create new report
    try {
      const reportPayload = {
        application_id: application.id,
        status: "draft",
        amount_received: formData.amount_received ? parseFloat(formData.amount_received) : null,
      };
      
      const newReport = await directus.request(
        createItem("reports" as never, reportPayload as never)
      );
      const createdReport = newReport as unknown as ReportRecord;
      setReport(createdReport);
      return createdReport.id;
    } catch (err) {
      console.error("Error creating report:", err);
      return null;
    }
  };

  const handleFileUpload = async (docType: ReportDocumentType, file: File) => {
    if (!application) return;
    
    setIsUploading(docType);
    setError(null);

    try {
      // Ensure report exists
      const reportId = await ensureReportExists();
      if (!reportId) {
        setError("Не вдалося створити звіт");
        return;
      }

      // Upload file to Directus with folder structure: applications/{EDRPOU}/{app_number}/report/
      const fileId = await uploadReportFile(
        file,
        application.edrpou_code || undefined,
        application.application_number || undefined
      );
      
      if (!fileId) {
        throw new Error("Failed to upload file");
      }

      // Create report_documents record
      const docRecord = await directus.request(
        createItem("report_documents" as never, {
          report_id: reportId,
          document_type: docType,
          file_id: fileId,
          file_name: file.name,
        } as never)
      );

      setDocuments(prev => ({
        ...prev,
        [docType]: docRecord as unknown as ReportDocumentRecord,
      }));
      
      setSuccessMessage(`Документ "${REPORT_DOCUMENT_LABELS[docType]}" завантажено`);
    } catch (err) {
      console.error("Error uploading file:", err);
      setError("Помилка завантаження файлу");
    } finally {
      setIsUploading(null);
    }
  };

  const handleDeleteDocument = async (docType: ReportDocumentType) => {
    const doc = documents[docType];
    if (!doc) return;

    try {
      await directus.request(deleteItem("report_documents" as never, doc.id));
      setDocuments(prev => ({ ...prev, [docType]: null }));
      setSuccessMessage(`Документ видалено`);
    } catch (err) {
      console.error("Error deleting document:", err);
      setError("Помилка видалення документа");
    }
  };

  // Equipment item handlers
  const handleAddEquipmentItem = () => {
    setEquipmentItems(prev => [
      ...prev,
      {
        tempId: `temp-${Date.now()}`,
        name: "",
        serial_number: "",
        quantity: 1,
        price_with_vat: 0,
        photo_id: null,
      },
    ]);
  };

  const handleUpdateEquipmentItem = (index: number, field: keyof EquipmentItemForm, value: string | number) => {
    setEquipmentItems(prev => {
      const updated = [...prev];
      const item = updated[index];
      if (item) {
        updated[index] = { ...item, [field]: value };
      }
      return updated;
    });
    setSuccessMessage(null);
  };

  const handleRemoveEquipmentItem = async (index: number) => {
    const item = equipmentItems[index];
    if (!item) return;
    
    // If item has an ID, delete from database
    if (item.id) {
      try {
        await directus.request(deleteItem("report_equipment" as never, item.id));
      } catch (err) {
        console.error("Error deleting equipment item:", err);
        setError("Помилка видалення обладнання");
        return;
      }
    }
    
    setEquipmentItems(prev => prev.filter((_, i) => i !== index));
    setSuccessMessage("Обладнання видалено");
  };

  const handleSaveEquipmentItem = async (index: number) => {
    const item = equipmentItems[index];
    if (!item) return;
    
    if (!item.name.trim()) {
      setError("Введіть назву обладнання");
      return;
    }

    // Ensure report exists
    const reportId = await ensureReportExists();
    if (!reportId) {
      setError("Не вдалося створити звіт");
      return;
    }

    setIsSavingEquipment(true);
    setError(null);

    try {
      const payload = {
        report_id: reportId,
        item_number: index + 1,
        name: item.name,
        serial_number: item.serial_number || null,
        quantity: item.quantity || 1,
        price_with_vat: item.price_with_vat || 0,
        photo_id: item.photo_id || null,
      };

      if (item.id) {
        // Update existing
        await directus.request(
          updateItem("report_equipment" as never, item.id, payload as never)
        );
        setSuccessMessage("Обладнання оновлено");
      } else {
        // Create new
        const result = await directus.request(
          createItem("report_equipment" as never, payload as never)
        ) as unknown as ReportEquipmentRecord;
        
        // Update local state with new ID
        setEquipmentItems(prev => {
          const updated = [...prev];
          const existingItem = updated[index];
          if (existingItem) {
            updated[index] = { ...existingItem, id: result.id, tempId: undefined };
          }
          return updated;
        });
        setSuccessMessage("Обладнання збережено");
      }
    } catch (err) {
      console.error("Error saving equipment item:", err);
      setError("Помилка збереження обладнання");
    } finally {
      setIsSavingEquipment(false);
    }
  };

  // Photo upload handler for equipment item
  const [uploadingPhotoIndex, setUploadingPhotoIndex] = useState<number | null>(null);
  
  const handleEquipmentPhotoUpload = async (index: number, file: File) => {
    if (!application) return;
    
    setUploadingPhotoIndex(index);
    setError(null);

    try {
      // Upload file to Directus with folder structure: applications/{EDRPOU}/{app_number}/report/
      const fileId = await uploadReportFile(
        file,
        application.edrpou_code || undefined,
        application.application_number || undefined
      );
      
      if (!fileId) {
        throw new Error("Failed to upload file");
      }

      // Update local state with photo_id
      setEquipmentItems(prev => {
        const updated = [...prev];
        const existingItem = updated[index];
        if (existingItem) {
          updated[index] = { 
            ...existingItem, 
            photo_id: fileId,
            photo_file_name: file.name,
          };
        }
        return updated;
      });
      
      setSuccessMessage("Фото обладнання завантажено");
    } catch (err) {
      console.error("Error uploading equipment photo:", err);
      setError("Помилка завантаження фото");
    } finally {
      setUploadingPhotoIndex(null);
    }
  };

  const handleRemoveEquipmentPhoto = (index: number) => {
    setEquipmentItems(prev => {
      const updated = [...prev];
      const existingItem = updated[index];
      if (existingItem) {
        updated[index] = { 
          ...existingItem, 
          photo_id: null,
          photo_file_name: undefined,
        };
      }
      return updated;
    });
    setSuccessMessage(null);
  };

  // Calculate total equipment cost
  const totalEquipmentCost = equipmentItems.reduce(
    (sum, item) => sum + Number(item.price_with_vat || 0),
    0
  );

  // Format money
  const formatMoney = (amount: number): string => {
    return new Intl.NumberFormat("uk-UA", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount) + " грн";
  };

  const handleSave = async (submit = false) => {
    if (!application) return;
    
    setIsSaving(true);
    setError(null);
    setSuccessMessage(null);

    // Validate required documents and equipment for submission
    if (submit) {
      const missingDocs = DOCUMENT_TYPES.filter(type => !documents[type]);
      if (missingDocs.length > 0) {
        setError(`Для подання звіту необхідно завантажити всі документи`);
        setIsSaving(false);
        return;
      }
      
      // Check equipment items
      const savedEquipment = equipmentItems.filter(eq => eq.id);
      if (savedEquipment.length === 0) {
        setError(`Для подання звіту необхідно додати та зберегти хоча б одне обладнання`);
        setIsSaving(false);
        return;
      }
      
      // Check if all equipment items are saved
      const unsavedEquipment = equipmentItems.filter(eq => !eq.id && eq.name.trim());
      if (unsavedEquipment.length > 0) {
        setError(`Збережіть усі додані обладнання перед поданням звіту`);
        setIsSaving(false);
        return;
      }
    }

    try {
      const reportPayload = {
        application_id: application.id,
        status: submit ? "submitted" : "draft",
        amount_received: formData.amount_received ? parseFloat(formData.amount_received) : null,
        amount_used: formData.amount_used ? parseFloat(formData.amount_used) : null,
        own_funds_used: formData.own_funds_used ? parseFloat(formData.own_funds_used) : null,
        unused_funds: formData.unused_funds ? parseFloat(formData.unused_funds) : null,
        equipment_installed: formData.equipment_installed,
        equipment_tested: formData.equipment_tested,
        installation_date: formData.installation_date || null,
        notes: formData.notes || null,
        ...(submit ? { submitted_at: new Date().toISOString() } : {}),
      };

      if (report) {
        // Update existing report
        await directus.request(
          updateItem("reports" as never, report.id, reportPayload as never)
        );
        setReport({ ...report, ...reportPayload } as ReportRecord);
      } else {
        // Create new report
        const newReport = await directus.request(
          createItem("reports" as never, reportPayload as never)
        );
        setReport(newReport as unknown as ReportRecord);
      }

      if (submit) {
        // Update application status
        await directus.request(
          updateItem("applications" as never, application.id, { status: "report_submitted" } as never)
        );
        setSuccessMessage("Звіт успішно подано!");
        setTimeout(() => {
          navigate(`/application/${application.id}/view`);
        }, 1500);
      } else {
        setSuccessMessage("Звіт збережено як чернетку");
      }
    } catch (err) {
      console.error("Error saving report:", err);
      setError("Помилка збереження звіту");
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <AppLayout userRole="applicant">
        <div className="w-full flex items-center justify-center min-h-[60vh]">
          <div className="flex flex-col items-center justify-center">
            <Spinner size="lg" />
            <p className="mt-4 text-muted-foreground">Завантаження...</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  if (error && !application) {
    return (
      <AppLayout userRole="applicant">
        <div className="w-full flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <p className="text-red-500 mb-4">{error}</p>
            <Button asChild variant="outline">
              <Link to="/applications">Повернутись до списку</Link>
            </Button>
          </div>
        </div>
      </AppLayout>
    );
  }

  if (!application) return null;

  const canEdit = !report || report.status === "draft";
  const uploadedCount = Object.values(documents).filter(Boolean).length;
  const totalCount = DOCUMENT_TYPES.length;
  const allDocsUploaded = uploadedCount === totalCount;
  const savedEquipmentCount = equipmentItems.filter(eq => eq.id).length;
  const hasEquipment = savedEquipmentCount > 0;
  const canSubmit = allDocsUploaded && hasEquipment;

  return (
    <AppLayout userRole="applicant">
      <div className="space-y-6">
        {/* Back link */}
        <Link
          to={`/application/${application.id}/view`}
          className="inline-flex items-center gap-1.5 text-[13px] text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Назад до заявки
        </Link>

        {/* Header */}
        <div>
          <h1 className="text-xl font-semibold tracking-tight">
            Звіт про виконання
          </h1>
          <p className="text-[13px] text-muted-foreground mt-1">
            Заявка {application.application_number || `#${application.id}`}
          </p>
        </div>

        {/* Success/Error Messages */}
        {successMessage && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-green-700 text-[13px]">
            {successMessage}
          </div>
        )}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-[13px]">
            {error}
          </div>
        )}

        {!canEdit && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-amber-700 text-[13px]">
            Звіт вже подано і не може бути змінений.
          </div>
        )}

        {/* Form */}
        <div className="bg-white rounded-2xl border-[3px] border-border/50 p-6 space-y-6">
          <h2 className="text-[15px] font-semibold">Фінансова інформація</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-[12px] font-medium text-muted-foreground mb-1">
                Отримано коштів (грн) *
              </label>
              <input
                type="number"
                value={formData.amount_received}
                onChange={(e) => handleInputChange("amount_received", e.target.value)}
                disabled={!canEdit}
                className={cn(
                  "w-full px-3 py-2 border rounded-lg text-[14px]",
                  canEdit ? "border-border" : "bg-slate-50 text-muted-foreground"
                )}
                placeholder="0"
              />
            </div>
            <div>
              <label className="block text-[12px] font-medium text-muted-foreground mb-1">
                Використано на обладнання (грн) *
              </label>
              <input
                type="number"
                value={formData.amount_used}
                onChange={(e) => handleInputChange("amount_used", e.target.value)}
                disabled={!canEdit}
                className={cn(
                  "w-full px-3 py-2 border rounded-lg text-[14px]",
                  canEdit ? "border-border" : "bg-slate-50 text-muted-foreground"
                )}
                placeholder="0"
              />
            </div>
            <div>
              <label className="block text-[12px] font-medium text-muted-foreground mb-1">
                Власні кошти (співфінансування) (грн)
              </label>
              <input
                type="number"
                value={formData.own_funds_used}
                onChange={(e) => handleInputChange("own_funds_used", e.target.value)}
                disabled={!canEdit}
                className={cn(
                  "w-full px-3 py-2 border rounded-lg text-[14px]",
                  canEdit ? "border-border" : "bg-slate-50 text-muted-foreground"
                )}
                placeholder="0"
              />
            </div>
            <div>
              <label className="block text-[12px] font-medium text-muted-foreground mb-1">
                Невикористаний залишок (грн)
              </label>
              <input
                type="number"
                value={formData.unused_funds}
                onChange={(e) => handleInputChange("unused_funds", e.target.value)}
                disabled={!canEdit}
                className={cn(
                  "w-full px-3 py-2 border rounded-lg text-[14px]",
                  canEdit ? "border-border" : "bg-slate-50 text-muted-foreground"
                )}
                placeholder="0"
              />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl border-[3px] border-border/50 p-6 space-y-6">
          <h2 className="text-[15px] font-semibold">Статус обладнання</h2>
          
          <div className="space-y-4">
            <label className="flex items-center gap-3 cursor-pointer group">
              <div className="relative">
                <input
                  type="checkbox"
                  checked={formData.equipment_installed}
                  onChange={(e) => handleInputChange("equipment_installed", e.target.checked)}
                  disabled={!canEdit}
                  className="peer sr-only"
                />
                <div className={`w-5 h-5 rounded-md border-2 transition-all duration-200 flex items-center justify-center
                  ${formData.equipment_installed 
                    ? 'bg-primary border-primary' 
                    : 'bg-white border-slate-300 group-hover:border-primary/50'
                  }
                  ${!canEdit ? 'opacity-50 cursor-not-allowed' : ''}
                  peer-focus-visible:ring-2 peer-focus-visible:ring-primary/30 peer-focus-visible:ring-offset-1`}
                >
                  {formData.equipment_installed && (
                    <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
              </div>
              <span className="text-[14px] select-none">Обладнання встановлено</span>
            </label>
            
            <label className="flex items-center gap-3 cursor-pointer group">
              <div className="relative">
                <input
                  type="checkbox"
                  checked={formData.equipment_tested}
                  onChange={(e) => handleInputChange("equipment_tested", e.target.checked)}
                  disabled={!canEdit}
                  className="peer sr-only"
                />
                <div className={`w-5 h-5 rounded-md border-2 transition-all duration-200 flex items-center justify-center
                  ${formData.equipment_tested 
                    ? 'bg-primary border-primary' 
                    : 'bg-white border-slate-300 group-hover:border-primary/50'
                  }
                  ${!canEdit ? 'opacity-50 cursor-not-allowed' : ''}
                  peer-focus-visible:ring-2 peer-focus-visible:ring-primary/30 peer-focus-visible:ring-offset-1`}
                >
                  {formData.equipment_tested && (
                    <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
              </div>
              <span className="text-[14px] select-none">Обладнання протестовано</span>
            </label>

            <div className="max-w-xs">
              <label className="block text-[12px] font-medium text-muted-foreground mb-1">
                Дата встановлення
              </label>
              <input
                type="date"
                value={formData.installation_date}
                onChange={(e) => handleInputChange("installation_date", e.target.value)}
                disabled={!canEdit}
                className={cn(
                  "w-full px-3 py-2 border rounded-lg text-[14px]",
                  canEdit ? "border-border" : "bg-slate-50 text-muted-foreground"
                )}
              />
            </div>
          </div>
        </div>

        {/* Equipment Items Section */}
        <div className="bg-white rounded-2xl border-[3px] border-border/50 p-6 space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-[15px] font-semibold">Придбане обладнання</h2>
              <p className="text-[12px] text-muted-foreground mt-1">
                Перелік фактично придбаного та встановленого обладнання
              </p>
            </div>
            {canEdit && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleAddEquipmentItem}
                className="gap-1.5"
              >
                <PlusIcon className="w-4 h-4" />
                Додати
              </Button>
            )}
          </div>

          {equipmentItems.length === 0 ? (
            <div className="text-center py-10 bg-gradient-to-b from-slate-50 to-slate-100/50 rounded-xl border-2 border-dashed border-slate-200">
              <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
                <svg className="w-7 h-7 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
              </div>
              <p className="text-[14px] font-medium text-muted-foreground">
                Обладнання ще не додано
              </p>
              <p className="text-[12px] text-muted-foreground/70 mt-1 mb-4">
                Додайте придбане обладнання для звіту
              </p>
              {canEdit && (
                <Button
                  type="button"
                  variant="default"
                  onClick={handleAddEquipmentItem}
                  className="gap-2 px-5"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                  </svg>
                  Додати обладнання
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {equipmentItems.map((item, index) => (
                <div
                  key={item.id || item.tempId}
                  className="p-4 border-2 border-border/50 rounded-xl space-y-4"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-[13px] font-medium text-muted-foreground">
                      #{index + 1}
                    </span>
                    {canEdit && (
                      <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => handleSaveEquipmentItem(index)}
                          disabled={isSavingEquipment || !item.name.trim()}
                          className="text-[12px] h-8"
                        >
                          {isSavingEquipment ? "..." : item.id ? "Оновити" : "Зберегти"}
                        </Button>
                        <button
                          type="button"
                          onClick={() => handleRemoveEquipmentItem(index)}
                          className="p-1.5 rounded-lg hover:bg-red-50 text-red-500 transition-colors"
                          title="Видалити"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <label className="block text-[12px] font-medium text-muted-foreground mb-1">
                        Найменування (марка, модель) *
                      </label>
                      <input
                        type="text"
                        value={item.name}
                        onChange={(e) => handleUpdateEquipmentItem(index, "name", e.target.value)}
                        disabled={!canEdit}
                        className={cn(
                          "w-full px-3 py-2 border rounded-lg text-[14px]",
                          canEdit ? "border-border" : "bg-slate-50 text-muted-foreground"
                        )}
                        placeholder="Наприклад: Генератор газовий Hyundai HHY 10000FGE 10 кВт"
                      />
                    </div>
                    <div>
                      <label className="block text-[12px] font-medium text-muted-foreground mb-1">
                        Серійний номер
                      </label>
                      <input
                        type="text"
                        value={item.serial_number}
                        onChange={(e) => handleUpdateEquipmentItem(index, "serial_number", e.target.value)}
                        disabled={!canEdit}
                        className={cn(
                          "w-full px-3 py-2 border rounded-lg text-[14px]",
                          canEdit ? "border-border" : "bg-slate-50 text-muted-foreground"
                        )}
                        placeholder="HYU2025-789012"
                      />
                    </div>
                    <div>
                      <label className="block text-[12px] font-medium text-muted-foreground mb-1">
                        Кількість
                      </label>
                      <input
                        type="number"
                        value={item.quantity}
                        onChange={(e) => handleUpdateEquipmentItem(index, "quantity", parseInt(e.target.value) || 1)}
                        disabled={!canEdit}
                        min={1}
                        className={cn(
                          "w-full px-3 py-2 border rounded-lg text-[14px]",
                          canEdit ? "border-border" : "bg-slate-50 text-muted-foreground"
                        )}
                      />
                    </div>
                    <div>
                      <label className="block text-[12px] font-medium text-muted-foreground mb-1">
                        Вартість з ПДВ (грн)
                      </label>
                      <input
                        type="number"
                        value={item.price_with_vat}
                        onChange={(e) => handleUpdateEquipmentItem(index, "price_with_vat", parseFloat(e.target.value) || 0)}
                        disabled={!canEdit}
                        min={0}
                        className={cn(
                          "w-full px-3 py-2 border rounded-lg text-[14px]",
                          canEdit ? "border-border" : "bg-slate-50 text-muted-foreground"
                        )}
                        placeholder="0"
                      />
                    </div>
                  </div>

                  {/* Photo upload for this equipment item */}
                  <div className="pt-3 border-t border-border/30">
                    <label className="block text-[12px] font-medium text-muted-foreground mb-2">
                      Фото обладнання
                    </label>
                    {item.photo_id ? (
                      <div className="flex items-center gap-3 p-3 bg-green-50 rounded-xl">
                        <div className="w-16 h-16 rounded-lg overflow-hidden bg-slate-100 shrink-0">
                          <img 
                            src={`${import.meta.env.VITE_DIRECTUS_URL || 'http://63.177.81.222:8055'}/assets/${item.photo_id}?width=128&height=128&fit=cover`}
                            alt="Фото обладнання"
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[12px] font-medium text-green-700 truncate">
                            {item.photo_file_name || "Фото завантажено"}
                          </p>
                          <p className="text-[10px] text-green-600">Завантажено</p>
                        </div>
                        {canEdit && (
                          <button
                            type="button"
                            onClick={() => handleRemoveEquipmentPhoto(index)}
                            className="p-2 rounded-lg hover:bg-green-100 transition-colors"
                          >
                            <TrashIcon className="w-4 h-4 text-green-600" />
                          </button>
                        )}
                      </div>
                    ) : canEdit ? (
                      uploadingPhotoIndex === index ? (
                        <div className="flex flex-col items-center justify-center p-4 border-2 border-dashed border-primary/50 rounded-xl bg-primary/5">
                          <Spinner size="sm" className="mb-2" />
                          <p className="text-[11px] font-medium text-primary">Завантаження...</p>
                        </div>
                      ) : (
                        <label className="flex flex-col items-center justify-center p-4 border-2 border-dashed border-border/50 rounded-xl cursor-pointer hover:border-primary/50 hover:bg-muted/30 transition-all">
                          <UploadIcon className="w-6 h-6 text-muted-foreground mb-1" />
                          <p className="text-[11px] font-medium text-center">
                            Перетягніть або <span className="text-primary">оберіть</span>
                          </p>
                          <p className="text-[10px] text-muted-foreground mt-0.5">JPG, PNG до 10 МБ</p>
                          <input
                            type="file"
                            accept=".jpg,.jpeg,.png"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) handleEquipmentPhotoUpload(index, file);
                              e.target.value = "";
                            }}
                            className="hidden"
                          />
                        </label>
                      )
                    ) : (
                      <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
                        <FileIcon className="w-5 h-5 text-slate-400 shrink-0" />
                        <p className="text-[12px] text-slate-500">Фото не завантажено</p>
                      </div>
                    )}
                  </div>
                  
                  {/* Item saved indicator */}
                  {item.id && (
                    <div className="flex items-center gap-1.5 text-green-600 text-[11px]">
                      <CheckCircleIcon className="w-4 h-4" />
                      Збережено
                    </div>
                  )}
                </div>
              ))}

              {/* Total cost */}
              <div className="flex justify-between items-center p-4 bg-primary/10 rounded-xl mt-4">
                <span className="text-[14px] font-medium">Загальна вартість обладнання</span>
                <span className="text-[16px] font-bold text-primary">
                  {formatMoney(totalEquipmentCost)}
                </span>
              </div>

              {/* Help text */}
              <p className="text-[11px] text-muted-foreground">
                * Натисніть "Зберегти" для кожного обладнання окремо, щоб зберегти його в базі
              </p>
            </div>
          )}
        </div>

        {/* Documents Upload Section */}
        <div className="space-y-6">
          <div>
            <h3 className="text-[15px] font-semibold mb-2">Необхідні документи</h3>
            <p className="text-[12px] text-muted-foreground mb-4">
              Завантажте всі необхідні документи для подання звіту
            </p>

            {/* Progress */}
            <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-xl mb-6">
              <div className="flex-1">
                <div className="flex justify-between mb-1">
                  <span className="text-[12px] font-medium">Завантажено документів</span>
                  <span className="text-[12px] font-semibold">{uploadedCount} / {totalCount}</span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className={cn(
                      "h-full transition-all rounded-full",
                      allDocsUploaded ? "bg-green-500" : "bg-primary"
                    )}
                    style={{ width: `${(uploadedCount / totalCount) * 100}%` }}
                  />
                </div>
              </div>
              {allDocsUploaded && (
                <CheckCircleIcon className="w-6 h-6 text-green-500 shrink-0" />
              )}
            </div>
          </div>

          {/* Document list */}
          <div className="grid gap-4">
            {DOCUMENT_TYPES.map((docType) => (
              <DocumentUploadCard
                key={docType}
                docType={docType}
                document={documents[docType] ?? null}
                onUpload={(file) => handleFileUpload(docType, file)}
                onRemove={() => handleDeleteDocument(docType)}
                isUploading={isUploading === docType}
                canEdit={canEdit}
              />
            ))}
          </div>

          {/* Help text */}
          <div className="bg-amber-50 rounded-xl border-2 border-amber-200 p-4">
            <p className="text-[12px] text-amber-700">
              <strong>Важливо:</strong> Всі документи повинні бути чіткими та читабельними. 
              Фото обладнання завантажуйте безпосередньо до кожної позиції в розділі "Придбане обладнання".
            </p>
          </div>
        </div>

        <div className="bg-white rounded-2xl border-[3px] border-border/50 p-6 space-y-4">
          <h2 className="text-[15px] font-semibold">Примітки</h2>
          <textarea
            value={formData.notes}
            onChange={(e) => handleInputChange("notes", e.target.value)}
            disabled={!canEdit}
            rows={3}
            className={cn(
              "w-full px-3 py-2 border rounded-lg text-[14px]",
              canEdit ? "border-border" : "bg-slate-50 text-muted-foreground"
            )}
            placeholder="Додаткова інформація про виконання проєкту..."
          />
        </div>

        {/* Actions */}
        {canEdit && (
          <div className="flex items-center gap-3 justify-end pt-4">
            <Button
              variant="outline"
              onClick={() => handleSave(false)}
              disabled={isSaving}
            >
              {isSaving ? "Збереження..." : "Зберегти чернетку"}
            </Button>
            <Button
              onClick={() => handleSave(true)}
              disabled={isSaving || !canSubmit}
              className="bg-primary hover:bg-primary/90"
              title={!canSubmit ? "Завантажте всі документи та додайте обладнання" : ""}
            >
              {isSaving ? "Подання..." : "Подати звіт"}
            </Button>
          </div>
        )}

        {canEdit && !canSubmit && (
          <div className="text-[12px] text-amber-600 text-right space-y-1">
            {!allDocsUploaded && (
              <p>* Для подання звіту необхідно завантажити всі документи ({uploadedCount}/{totalCount})</p>
            )}
            {!hasEquipment && (
              <p>* Для подання звіту необхідно додати та зберегти хоча б одне обладнання</p>
            )}
          </div>
        )}
      </div>
    </AppLayout>
  );
}

export default ReportWizard;
